// Row-level filter matching the SummaryStrip card predicates. Runs
// AFTER groupDeliverables so parent/child grouping is preserved —
// a parent survives when at least one of its children passes the
// predicate, and its original children are mirrored onto
// `allChildren` so the OccurrenceStrip can still render the full
// history while the expanded row list shows only the matches.
//
// Predicates use `deriveDeliverableStatus` so the tile count, the
// filter chip badge, and the row pill colour cannot drift:
//   - overdue: derived `overdue` ONLY (true overdue, supplier chase)
//   - due-this-week: stored `not-started` AND dueDate in [today, today+7d]
//   - has-failed-acceptance: failedAcceptanceCount > 0 (any deliverable
//     that has bounced acceptance at least once — the contract-manager
//     triage cohort)

import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import type { ChildRow, ParentRow, Row } from "./groupDeliverables";
import type { Deliverable } from "../../../../types";

export type SummaryFilterKey =
  | "overdue"
  | "due-this-week"
  | "has-failed-acceptance";

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function makePredicate(
  filter: SummaryFilterKey,
  today: Date,
): (d: Deliverable) => boolean {
  const todayMs = startOfUTCDay(today);
  const sevenDaysMs = todayMs + 7 * 86_400_000;

  switch (filter) {
    case "overdue":
      return (d) => {
        if (d.archivedAt) return false;
        if (d.recurrenceRule) return false;
        const derived = deriveDeliverableStatus(
          d.status,
          d.dueDate,
          today,
          d.submittedAt,
        );
        return derived === "overdue";
      };
    case "due-this-week":
      return (d) => {
        if (d.archivedAt) return false;
        if (d.recurrenceRule) return false;
        if (d.status !== "not-started" || !d.dueDate) return false;
        const due = isoToUTCDay(d.dueDate);
        return due !== null && due >= todayMs && due <= sevenDaysMs;
      };
    case "has-failed-acceptance":
      return (d) => {
        if (d.archivedAt) return false;
        if (d.recurrenceRule) return false;
        return (d.failedAcceptanceCount ?? 0) > 0;
      };
  }
}

export function applySummaryFilter(
  rows: Row[],
  filter: SummaryFilterKey | null,
  today: Date = new Date(),
): Row[] {
  if (filter === null) return rows;
  const predicate = makePredicate(filter, today);

  const result: Row[] = [];
  for (const row of rows) {
    if (row.kind === "one-off" || row.kind === "orphan") {
      // Orphans share filter semantics with one-offs: the predicate
      // applies to the orphan's own deliverable, not to a parent
      // schedule.
      if (predicate(row.deliverable)) result.push(row);
      continue;
    }
    if (row.kind === "parent") {
      const matching = row.children.filter((c) => predicate(c.deliverable));
      if (matching.length === 0) continue;
      const filtered: ParentRow = {
        kind: "parent",
        deliverable: row.deliverable,
        children: matching,
        allChildren: row.children,
      };
      result.push(filtered);
      continue;
    }
    // ChildRow is only produced from within a ParentRow; it does not
    // appear at the top level of groupDeliverables output. Guard with
    // the predicate in case a caller ever passes a flattened list.
    const childRow = row as ChildRow;
    if (predicate(childRow.deliverable)) {
      result.push(childRow);
    }
  }

  return result;
}
